import { Frame, FrameOptionConsolidated } from '../../../../types/Products';
import {
  FramingChoice,
  FramingProduct,
  FramingSelection,
  GlazingChoice,
  MountingChoice,
} from '../types';

type ConsolidatedVariants =
  FrameOptionConsolidated['variants'][number]['consolidatedVariants'];

/** Sum of a variant's consolidated line items (price × quantity). */
export const sumVariants = (
  consolidatedVariants: ConsolidatedVariants,
): number =>
  consolidatedVariants.reduce(
    (total, { price, quantity }) => total + price * quantity,
    0,
  );

/**
 * Cheapest variant total of a frame style, used to price one style against
 * another regardless of which glazing/mounting is currently selected.
 */
export const getStylePrice = (
  option: FrameOptionConsolidated | null,
): number => {
  if (!option?.variants.length) return 0;

  return Math.min(
    ...option.variants.map(({ consolidatedVariants }) => {
      return sumVariants(consolidatedVariants);
    }),
  );
};

/** Total price of a resolved frame (0 when unframed). */
export const getFramePrice = (frame: Frame | null): number =>
  frame ? sumVariants(frame.variant.consolidatedVariants) : 0;

/** The mounting a frame reports when it doesn't offer a mounting axis at all. */
export const STANDARD_MOUNTING = 'Standard';

/**
 * Frame variant titles encode two axes as "<glazing> / <mounting>". Frames that
 * only vary by glazing carry no mounting segment and default to
 * `STANDARD_MOUNTING`.
 */
export const parseVariantTitle = (
  title: string,
): { glazing: string; mounting: string } => {
  const [glazing, mounting] = title.split('/').map((part) => part.trim());
  return { glazing, mounting: mounting || STANDARD_MOUNTING };
};

/**
 * One representative glazing choice per glazing name. When a mounting is given
 * the representative is that glazing's variant in the same mounting, so prices
 * compare like for like; otherwise the first variant wins. Order follows the
 * option's variant order.
 */
export const getGlazingChoices = (
  option: FrameOptionConsolidated | null,
  preferredMounting?: string | null,
): GlazingChoice[] => {
  if (!option) return [];

  const seen = new Set<string>();
  const choices: GlazingChoice[] = [];

  option.variants.forEach((variant) => {
    const { glazing } = parseVariantTitle(variant.title);
    if (seen.has(glazing)) return;
    seen.add(glazing);

    const preferred =
      (preferredMounting
        ? option.variants.find((candidate) => {
            const parts = parseVariantTitle(candidate.title);
            return (
              parts.glazing === glazing && parts.mounting === preferredMounting
            );
          })
        : undefined) ?? variant;

    choices.push({
      glazing,
      variantTitle: preferred.title,
      total: sumVariants(preferred.consolidatedVariants),
    });
  });

  return choices;
};

/**
 * The mounting choices offered for one glazing of a frame style. A frame whose
 * variants carry no mounting segment yields a single "Standard" choice, which
 * the picker treats as nothing to choose.
 */
export const getMountingChoices = (
  option: FrameOptionConsolidated | null,
  glazing: string | null,
): MountingChoice[] => {
  if (!option || !glazing) return [];

  const seen = new Set<string>();
  const choices: MountingChoice[] = [];

  option.variants.forEach((variant) => {
    const parts = parseVariantTitle(variant.title);
    if (parts.glazing !== glazing) return;
    if (seen.has(parts.mounting)) return;
    seen.add(parts.mounting);
    choices.push({
      mounting: parts.mounting,
      variantTitle: variant.title,
      total: sumVariants(variant.consolidatedVariants),
      image: variant.consolidatedVariants[0]?.image ?? null,
    });
  });

  return choices;
};

/** Resolve a `Frame` from a frame style option and a chosen variant title. */
export const buildFrame = (
  option: FrameOptionConsolidated,
  variantTitle: string,
): Frame | null => {
  const variant = option.variants.find(({ title }) => title === variantTitle);
  if (!variant) return null;

  return {
    title: option.title,
    description: option.description,
    images: option.images,
    variant,
  };
};

/**
 * Resolve an abstract style+glazing+mounting `FramingChoice` against a single
 * product's own frame options. Used when applying one choice across multiple
 * works, where each product carries its own Shopify variant ids.
 *
 * Returns `undefined` (pending) when the product doesn't offer the exact
 * combination, so an "apply to all" that doesn't fit a work leaves it visibly
 * unselected rather than silently framing it in a style the user never chose.
 * The "apply to all" caller preserves a work's existing selection instead of
 * letting this `undefined` clobber a choice that work already had.
 */
export const resolveChoiceForProduct = (
  product: FramingProduct,
  choice: FramingChoice,
): FramingSelection => {
  if (choice === null) return null;

  const option = product.frameOptions.find(
    ({ title }) => title === choice.styleTitle,
  );
  if (!option) return undefined;

  const glazingChoice = getGlazingChoices(option, choice.mounting).find(
    ({ glazing }) => glazing === choice.glazing,
  );
  if (!glazingChoice) return undefined;

  if (choice.mounting) {
    const mountingChoice = getMountingChoices(option, choice.glazing).find(
      ({ mounting }) => mounting === choice.mounting,
    );
    if (!mountingChoice) return undefined;

    return buildFrame(option, mountingChoice.variantTitle);
  }

  return buildFrame(option, glazingChoice.variantTitle);
};

/**
 * The framing state a product should start in:
 * - an explicit `initialFrame` (frame or unframed) always wins;
 * - otherwise framed-by-default products start on their first option;
 * - otherwise nothing is chosen yet (`undefined`).
 */
export const getInitialSelection = (
  product: FramingProduct,
): FramingSelection => {
  if (product.initialFrame !== undefined) return product.initialFrame;

  if (product.framedByDefault && product.frameOptions.length > 0) {
    const [firstOption] = product.frameOptions;
    const [firstVariant] = firstOption.variants;
    return firstVariant
      ? buildFrame(firstOption, firstVariant.title)
      : undefined;
  }

  return undefined;
};

/** Product line total plus the selected frame's price. */
export const getFramingSubtotal = (
  productSubtotal: number,
  frame: Frame | null,
): number => productSubtotal + getFramePrice(frame);

export const priceSign = (amount: number): string | undefined => {
  if (amount > 0) return '+';
  if (amount < 0) return '-';
  return undefined;
};
